/**
 * Graceful Creations — wishlist
 *
 * Saves product handles to localStorage. No app, no customer account, nothing
 * stored on the shop: it survives a refresh and a return visit on the same
 * device, and deliberately does not follow anyone to another one.
 *
 * Uses a delegated click listener rather than binding each button, so hearts
 * that arrive later — a paginated collection, a quick-add modal, the theme
 * editor re-rendering a section — work without re-initialising anything.
 */

const KEY = 'gc:wishlist';
const EVENT = 'gc:wishlist:change';

/** Reading can throw in private mode or with site data blocked. Never let
 *  storage take the page down with it. */
function read() {
  try {
    const raw = window.localStorage.getItem(KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((h) => typeof h === 'string') : [];
  } catch (e) {
    return [];
  }
}

function write(list) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(list));
  } catch (e) {
    // Quota or a browser refusing storage. The in-page state below still
    // reflects the click, it just will not survive the next load.
  }
}

function has(handle) {
  return read().indexOf(handle) !== -1;
}

function toggle(handle) {
  const list = read();
  const at = list.indexOf(handle);
  if (at === -1) {
    list.push(handle);
  } else {
    list.splice(at, 1);
  }
  write(list);
  return at === -1;
}

/** Paint a single button from stored state. */
function paint(button) {
  const handle = button.getAttribute('data-gc-wish-handle');
  if (!handle) return;
  const saved = has(handle);
  button.setAttribute('aria-pressed', saved ? 'true' : 'false');
  button.classList.toggle('is-saved', saved);
  button.setAttribute(
    'title',
    saved ? 'Saved to wishlist' : 'Save to wishlist'
  );
}

function paintAll(root) {
  (root || document).querySelectorAll('[data-gc-wish]').forEach(paint);
}

/** Anywhere showing a count, e.g. a header link. */
function paintCounts() {
  const n = read().length;
  document.querySelectorAll('[data-gc-wish-count]').forEach((el) => {
    el.textContent = String(n);
    el.toggleAttribute('hidden', n === 0);
  });
}

function refresh(root) {
  paintAll(root);
  paintCounts();
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-gc-wish]');
  if (!button) return;

  // The heart sits on top of the card's link, so a click here must not
  // navigate to the product.
  event.preventDefault();
  event.stopPropagation();

  const handle = button.getAttribute('data-gc-wish-handle');
  if (!handle) return;

  const nowSaved = toggle(handle);
  refresh();

  button.classList.remove('gc-wish--pop');
  // Force a reflow so the animation restarts on a repeated click.
  void button.offsetWidth;
  if (nowSaved) button.classList.add('gc-wish--pop');

  document.dispatchEvent(
    new CustomEvent(EVENT, { detail: { handle, saved: nowSaved, list: read() } })
  );
});

// Another tab changed the list.
window.addEventListener('storage', (event) => {
  if (event.key === KEY) refresh();
});

// Sections re-rendered by the theme editor arrive without their state painted.
document.addEventListener('shopify:section:load', (event) => refresh(event.target));

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => refresh(), { once: true });
} else {
  refresh();
}

// Exposed so a wishlist page can read the same list rather than duplicating it.
window.gcWishlist = { read, has, toggle, refresh, KEY, EVENT };
